// src/pages/FeeStructure.tsx
import { useState, useEffect } from 'react';
import { SidebarProvider } from "@/components/ui/sidebar";
import GuardianHeader from "./GuardianHeader";
import GuardianSidebar from "./GuardianSidebar";
import { Download, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, IdTokenResult } from "firebase/auth";
import { useToast } from "@/hooks/use-toast";

// === Types ===
interface FeeCategory {
  name: string;
  term1: number;
  term2: number;
  term3: number;
  paidOnce?: boolean;
  daySchoolTerm1?: number;
  daySchoolTerm2?: number;
  daySchoolTerm3?: number;
}
interface LevelFees {
  level: string;
  categories: FeeCategory[];
  classes: string[];
  feeType: string;
  isGovernmentSponsored?: boolean;
  governmentSponsorshipAmount?: number;
  termIdsByClass?: Record<string, { term1: number; term2: number; term3: number }>;
}

interface DeployedFeeStructure {
  levelFees: LevelFees[];
  deployedAt: any;
  showGovernmentAmount: boolean;
  insights?: string;
  importantNotes?: string;
  termIdsByClass?: Record<string, { term1: number; term2: number; term3: number }>;
}

// === Helpers ===
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 0,
  }).format(amount);

const calculateTermTotal = (
  categories: FeeCategory[],
  term: "term1" | "term2" | "term3",
  feeType: "boarding" | "day" = "boarding"
) => {
  return categories.reduce((sum, c) => {
    if (feeType === "day") {
      const dayTerm = term === "term1" ? c.daySchoolTerm1 : term === "term2" ? c.daySchoolTerm2 : c.daySchoolTerm3;
      return sum + (dayTerm ?? c[term]);
    }
    return sum + c[term];
  }, 0);
};

// === PDF Generator (CDN-loaded) ===
const generatePDF = async (deployedStructure: DeployedFeeStructure | null) => {
  if (!deployedStructure) {
    alert("No fee structure available to generate PDF.");
    return;
  }

  const loadScript = (src: string) =>
    new Promise<void>((resolve, reject) => {
      // Check if already loaded
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });

  try {
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
    
    // Wait for scripts to initialize and check multiple locations
    await new Promise(resolve => setTimeout(resolve, 200));
    
    let jsPDF = (window as any).jsPDF;
    
    // Check different possible locations where jsPDF might be exposed
    if (!jsPDF && (window as any).jspdf) {
      jsPDF = (window as any).jspdf.jsPDF;
    }
    
    if (!jsPDF) {
      console.error("Available window properties:", Object.keys(window).filter(k => k.toLowerCase().includes('pdf')));
      throw new Error("jsPDF library not loaded. Please check your internet connection and try again.");
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    const addHeader = () => {
      doc.setFillColor(37, 99, 235);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("School Fee Structure", pageWidth / 2, 13, { align: "center" });
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Academic Year 2024/2025", pageWidth / 2, 22, { align: "center" });
    };
    addHeader();

    let y = 40;
    deployedStructure.levelFees.forEach((lvl, i) => {
      if (i > 0) {
        doc.addPage();
        addHeader();
        y = 40;
      }

      doc.setFillColor(248, 250, 252);
      doc.rect(10, y - 5, pageWidth - 20, 10, "F");
      doc.setTextColor(37, 99, 235);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`${lvl.level} Level`, 14, y + 2);
      y += 12;

      // Format currency with proper spacing: KSh 150,000
      const formatPDFCurrency = (amount: number) => {
        if (amount === 0) return "-";
        return "KSh " + amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      };

      const body = lvl.categories.map((c) => [
        c.paidOnce ? `${c.name} *` : c.name,
        formatPDFCurrency(c.term1),
        formatPDFCurrency(c.term2),
        formatPDFCurrency(c.term3),
      ]);

      const t1 = calculateTermTotal(lvl.categories, "term1");
      const t2 = calculateTermTotal(lvl.categories, "term2");
      const t3 = calculateTermTotal(lvl.categories, "term3");
      body.push(
        ["TERM TOTAL", formatPDFCurrency(t1), formatPDFCurrency(t2), formatPDFCurrency(t3)],
        ["ANNUAL TOTAL", formatPDFCurrency(t1 + t2 + t3), "-", "-"]
      );

      (doc as any).autoTable({
        startY: y,
        head: [["Fee Category", "Term 1", "Term 2", "Term 3"]],
        body,
        theme: "striped",
        headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { cellWidth: 70, halign: "left" },
          1: { cellWidth: 40, halign: "center" },
          2: { cellWidth: 40, halign: "center" },
          3: { cellWidth: 40, halign: "center" },
        },
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    });

    // Add insights and notes on a new page if they exist
    if (deployedStructure.insights || deployedStructure.importantNotes) {
      doc.addPage();
      
      // Reuse header for notes page
      doc.setFillColor(37, 99, 235);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("School Fee Structure", pageWidth / 2, 13, { align: "center" });
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Academic Year 2024/2025", pageWidth / 2, 22, { align: "center" });

      let notesY = 45;

      // Fee Structure Insights
      if (deployedStructure.insights) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, notesY - 2, pageWidth - 28, 10, "F");
        doc.setTextColor(37, 99, 235);
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.text("Fee Structure Insights", 18, notesY + 5);
        notesY += 15;

        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "normal");

        const insights = deployedStructure.insights.split('\n').filter(line => line.trim());
        insights.forEach(line => {
          doc.text(line, 18, notesY);
          notesY += 5;
        });

        notesY += 10;
      }

      // Important Notes
      if (deployedStructure.importantNotes) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, notesY - 2, pageWidth - 28, 10, "F");
        doc.setTextColor(37, 99, 235);
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.text("Important Notes", 18, notesY + 5);
        notesY += 15;

        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "normal");

        const notes = deployedStructure.importantNotes.split('\n').filter(line => line.trim());
        notes.forEach(line => {
          doc.text(line, 18, notesY);
          notesY += 5;
        });
      }
    }

    doc.save("School-Fee-Structure-2024-2025.pdf");
  } catch (error) {
    console.error("PDF generation failed:", error);
    alert("Failed to generate PDF. Please try again.");
  }
};

// Content-only component for embedding inside GuardianFinance
export function FeeStructureContent({ schoolID: propSchoolID }: { schoolID?: string | null }) {
  const [deployedStructure, setDeployedStructure] = useState<DeployedFeeStructure | null>(null);
  const [loading, setLoading] = useState(true);
  const [schoolDetails, setSchoolDetails] = useState<{type?: string} | null>(null);
  const [feeTypeView, setFeeTypeView] = useState<"boarding" | "day">("boarding");
  const { toast } = useToast();

  useEffect(() => {
    if (propSchoolID) {
      fetchDeployedStructure(propSchoolID);
      return;
    }

    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const tokenResult: IdTokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims;
          
          // Use top-level schoolID like GuardianFinance does
          const userSchoolId = claims.schoolID as string;
          
          if (userSchoolId) {
            await fetchDeployedStructure(userSchoolId);
          } else {
            toast({
              title: "No School Link",
              description: "Your account is not linked to any school yet.",
              variant: "default",
            });
            setLoading(false);
          }
        } catch (error) {
          console.error("Error fetching user claims:", error);
          toast({
            title: "Error",
            description: "Failed to load user information.",
            variant: "destructive",
          });
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [toast]);

  const fetchDeployedStructure = async (schoolId: string) => {
    try {
      setLoading(true);
      
      // Fetch school details to get school type
      const schoolDocRef = doc(db, `schools/${schoolId}/schoolDetails/main`);
      const schoolDoc = await getDoc(schoolDocRef);
      if (schoolDoc.exists()) {
        setSchoolDetails(schoolDoc.data());
      }
      
      const deployedDocRef = doc(db, `schools/${schoolId}/deployedFeeStructures/current`);
      const deployedDoc = await getDoc(deployedDocRef);

      if (deployedDoc.exists()) {
        const data = deployedDoc.data() as DeployedFeeStructure;
        console.log("Fetched deployed structure:", data);
        
        // Validate that we have the expected structure
        if (!data.levelFees || !Array.isArray(data.levelFees)) {
          console.error("Invalid fee structure format - missing or invalid levelFees array:", data);
          toast({
            title: "Invalid Data",
            description: "Fee structure data is in an unexpected format.",
            variant: "destructive",
          });
          setDeployedStructure(null);
        } else {
          setDeployedStructure(data);
        }
      } else {
        console.log("No deployed fee structure found");
        toast({
          title: "No Fee Structure",
          description: "No fee structure has been deployed yet.",
          variant: "default",
        });
        setDeployedStructure(null);
      }
    } catch (error) {
      console.error("Error fetching deployed fee structure:", error);
      toast({
        title: "Error",
        description: "Failed to load fee structure.",
        variant: "destructive",
      });
      setDeployedStructure(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!deployedStructure || !deployedStructure.levelFees || deployedStructure.levelFees.length === 0) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <Card className="m-2 border-2 border-gray-300 dark:border-gray-600">
          <CardContent className="p-12 text-center">
            <p className="text-gray-600 dark:text-gray-400">No fee structure has been deployed yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 m-2">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            School Fee Structure
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">Academic Year 2024/2025</p>
        </div>
        <Button onClick={() => generatePDF(deployedStructure)} size="lg" className="gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
          <Download className="h-5 w-5" />
          Download PDF
        </Button>
      </div>

      {/* Toggle for Boarding/Day School Fees - Only show for Mixed schools */}
      {schoolDetails?.type === "Mixed (Day & Boarding)" && (
        <div className="m-2">
          <Tabs value={feeTypeView} onValueChange={(value) => setFeeTypeView(value as "boarding" | "day")} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
              <TabsTrigger value="boarding" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Boarding School Fees
              </TabsTrigger>
              <TabsTrigger value="day" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
                Day School Fees
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Render each level as a separate card, just like DeployedFeeStructures */}
      {deployedStructure.levelFees.map((level) => {
        // Check if data exists for the selected view
        const hasDaySchoolFees = level.categories.some(cat => 
          cat.daySchoolTerm1 !== undefined || cat.daySchoolTerm2 !== undefined || cat.daySchoolTerm3 !== undefined
        );
        const hasBoardingFees = level.categories.some(cat => 
          cat.term1 !== undefined || cat.term2 !== undefined || cat.term3 !== undefined
        );
        const showNoDataMessage = schoolDetails?.type === "Mixed (Day & Boarding)" && (
          (feeTypeView === "day" && !hasDaySchoolFees) ||
          (feeTypeView === "boarding" && !hasBoardingFees)
        );

        const t1 = calculateTermTotal(level.categories, "term1", feeTypeView);
        const t2 = calculateTermTotal(level.categories, "term2", feeTypeView);
        const t3 = calculateTermTotal(level.categories, "term3", feeTypeView);
        const annualTotal = t1 + t2 + t3;
        const netPayable = level.isGovernmentSponsored && level.governmentSponsorshipAmount 
          ? annualTotal - level.governmentSponsorshipAmount 
          : annualTotal;

        return (
          <Card key={level.level} className="m-2 border-2 border-gray-300 dark:border-gray-600 shadow-md">
            <CardHeader className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-300 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{level.level}</CardTitle>
                {schoolDetails?.type === "Mixed (Day & Boarding)" && (
                  <Badge 
                    variant="outline" 
                    className={`${
                      feeTypeView === "boarding" 
                        ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-500" 
                        : "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-500"
                    }`}
                  >
                    {feeTypeView === "boarding" ? "Boarding Fees" : "Day School Fees"}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6 bg-white dark:bg-gray-800">
              {showNoDataMessage ? (
                <div className="p-8 text-center bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg">
                  <div className="flex flex-col items-center gap-4">
                    <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-4">
                      <XCircle className="h-12 w-12 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-amber-900 dark:text-amber-200 mb-2">
                        {feeTypeView === "day" ? "Day School Fees" : "Boarding School Fees"} Not Recorded
                      </h3>
                      <p className="text-amber-800 dark:text-amber-300">
                        Fee structure for {feeTypeView === "day" ? "day school students" : "boarding school students"} has not been recorded for {level.level}.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
              <div className="overflow-x-auto border-2 border-gray-200 dark:border-gray-700 rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-300 dark:border-gray-600">
                      <TableHead className="font-bold text-gray-900 dark:text-white">Fee Category</TableHead>
                      <TableHead className="text-right font-bold text-gray-900 dark:text-white">Term 1</TableHead>
                      <TableHead className="text-right font-bold text-gray-900 dark:text-white">Term 2</TableHead>
                      <TableHead className="text-right font-bold text-gray-900 dark:text-white">Term 3</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {level.categories.map((cat, idx) => {
                      const displayTerm1 = feeTypeView === "day" ? (cat.daySchoolTerm1 ?? cat.term1) : cat.term1;
                      const displayTerm2 = feeTypeView === "day" ? (cat.daySchoolTerm2 ?? cat.term2) : cat.term2;
                      const displayTerm3 = feeTypeView === "day" ? (cat.daySchoolTerm3 ?? cat.term3) : cat.term3;
                      
                      return (
                      <TableRow key={idx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          {cat.name}
                          {cat.paidOnce && <Badge variant="outline" className="ml-2 text-xs border-purple-500 text-purple-700 dark:text-purple-300">Paid Once</Badge>}
                        </TableCell>
                        <TableCell className="text-right text-gray-700 dark:text-gray-300">{formatCurrency(displayTerm1)}</TableCell>
                        <TableCell className="text-right text-gray-700 dark:text-gray-300">{formatCurrency(displayTerm2)}</TableCell>
                        <TableCell className="text-right text-gray-700 dark:text-gray-300">{formatCurrency(displayTerm3)}</TableCell>
                      </TableRow>
                      );
                    })}
                    <TableRow className="font-semibold bg-gray-200 dark:bg-gray-700 border-y-2 border-gray-300 dark:border-gray-600">
                      <TableCell className="text-gray-900 dark:text-white">TERM TOTAL</TableCell>
                      <TableCell className="text-right text-gray-900 dark:text-white">{formatCurrency(t1)}</TableCell>
                      <TableCell className="text-right text-gray-900 dark:text-white">{formatCurrency(t2)}</TableCell>
                      <TableCell className="text-right text-gray-900 dark:text-white">{formatCurrency(t3)}</TableCell>
                    </TableRow>
                    <TableRow className="font-bold bg-gradient-to-r from-blue-100 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/20 border-y-2 border-blue-300 dark:border-blue-700">
                      <TableCell className="text-blue-900 dark:text-blue-100">ANNUAL TOTAL</TableCell>
                      <TableCell className="text-right text-blue-900 dark:text-blue-100">{formatCurrency(annualTotal)}</TableCell>
                      <TableCell className="text-right text-blue-900 dark:text-blue-100">-</TableCell>
                      <TableCell className="text-right text-blue-900 dark:text-blue-100">-</TableCell>
                    </TableRow>
                    {deployedStructure.showGovernmentAmount && level.isGovernmentSponsored && level.governmentSponsorshipAmount && level.governmentSponsorshipAmount > 0 && (
                      <>
                        <TableRow className="bg-gradient-to-r from-green-100 to-green-50 dark:from-green-900/30 dark:to-green-900/20 border-y border-green-300 dark:border-green-700">
                          <TableCell className="font-semibold text-green-800 dark:text-green-300">GOVERNMENT SPONSORSHIP</TableCell>
                          <TableCell className="text-right font-semibold text-green-800 dark:text-green-300">
                            -{formatCurrency(level.governmentSponsorshipAmount)}
                          </TableCell>
                          <TableCell className="text-right text-green-800 dark:text-green-300">-</TableCell>
                          <TableCell className="text-right text-green-800 dark:text-green-300">-</TableCell>
                        </TableRow>
                        <TableRow className="font-bold bg-gradient-to-r from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-900/20 border-y-2 border-purple-300 dark:border-purple-700">
                          <TableCell className="text-purple-900 dark:text-purple-100">NET AMOUNT PAYABLE (After Govt Sponsorship)</TableCell>
                          <TableCell className="text-right text-purple-900 dark:text-purple-100">
                            {formatCurrency(netPayable)}
                          </TableCell>
                          <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                          <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                        </TableRow>
                      </>
                    )}
                    {!deployedStructure.showGovernmentAmount && level.isGovernmentSponsored && level.governmentSponsorshipAmount && level.governmentSponsorshipAmount > 0 && (
                      <TableRow className="font-bold bg-gradient-to-r from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-900/20 border-y-2 border-purple-300 dark:border-purple-700">
                        <TableCell className="text-purple-900 dark:text-purple-100">AMOUNT PAYABLE</TableCell>
                        <TableCell className="text-right text-purple-900 dark:text-purple-100">
                          {formatCurrency(netPayable)}
                        </TableCell>
                        <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                        <TableCell className="text-right text-purple-900 dark:text-purple-100">-</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Fee Structure Insights - styled like DeployedFeeStructures */}
      {deployedStructure.insights && (
        <Card className="m-2 border-2 border-indigo-300 dark:border-indigo-700 shadow-md">
          <CardHeader className="bg-gradient-to-r from-indigo-100 to-indigo-50 dark:from-indigo-900/40 dark:to-indigo-900/20 border-b-2 border-indigo-300 dark:border-indigo-700">
            <CardTitle className="text-xl font-bold text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
              Fee Structure Insights
              <Badge variant="secondary" className="bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 border-indigo-400 dark:border-indigo-600">Analysis</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 bg-white dark:bg-gray-800">
            <div className="space-y-3">
              {deployedStructure.insights.split('\n').map((line, index) => (
                line.trim() && (
                  <div key={index} className="flex items-start gap-3 p-4 m-2 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg border-l-4 border-indigo-500 shadow-sm">
                    <div className="w-2 h-2 rounded-full bg-indigo-600 dark:bg-indigo-400 mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-800 dark:text-gray-200">{line}</p>
                  </div>
                )
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Important Notes - styled like DeployedFeeStructures */}
      {deployedStructure.importantNotes && (
        <Card className="m-2 border-2 border-amber-300 dark:border-amber-700 shadow-md">
          <CardHeader className="bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-900/40 dark:to-amber-900/20 border-b-2 border-amber-300 dark:border-amber-700">
            <CardTitle className="text-xl font-bold text-amber-900 dark:text-amber-100">Important Notes</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 bg-white dark:bg-gray-800">
            <ul className="space-y-3">
              {deployedStructure.importantNotes.split('\n').map((line, index) => (
                line.trim() && (
                  <li key={index} className="flex items-start gap-3 p-3 m-2 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg border-l-4 border-amber-500 shadow-sm">
                    <span className="text-amber-600 dark:text-amber-400 font-bold">•</span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">{line}</span>
                  </li>
                )
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Full page wrapper keeping sidebar and header (used when routed directly)
export default function FeeStructure() {
  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <GuardianSidebar />
        <div className="flex flex-col flex-1 h-full w-full">
          <GuardianHeader />
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900">
            <FeeStructureContent />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}